import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { fetchNodeTimeseries } from '../../api';
import { PANEL_IDS } from '../../config/panelRegistry';
import { usePanelSettings } from '../../contexts/PanelSettingsContext';
import InfoHint from '../InfoHint';
import { AXIS, PRIMARY_COLOR, SEMANTIC_COLORS } from '../nodes/nodeChartTheme';
import useNodeRequest from '../nodes/useNodeRequest';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ErrorText, GranularitySelect, MetricToggle, MutedText } from './callTypesControls';
import {
  buildPairTrend,
  callsNoun,
  categoryFilter,
  defaultGranularity,
  formatCount,
  formatPercent,
  previousPeriod
} from './callTypesModel';

const METRICS = [
  { id: 'calls', label: 'Количество' },
  { id: 'share', label: 'Процент' }
];

const DATE_FORMAT = 'yyyy-MM-dd';

const fetchPairSeries = async ({ typeChart, outcomeChart, typeFilter, current, previous }) => {
  const seriesOf = (slice, chart, nodeFilter) =>
    fetchNodeTimeseries({
      ...slice,
      node_id: chart.nodeId,
      output_key: chart.outputKey,
      granularity: 'day',
      node_filter: nodeFilter
    });
  const [type, outcome, previousType, previousOutcome] = await Promise.all([
    seriesOf(current, typeChart),
    seriesOf(current, outcomeChart, typeFilter),
    seriesOf(previous, typeChart),
    seriesOf(previous, outcomeChart, typeFilter)
  ]);

  return { type, outcome, previousType, previousOutcome };
};

const describeDifference = (metric, { calls, typeCalls, previousCalls, previousTypeCalls }) => {
  if (metric === 'calls') {
    const difference = calls - previousCalls;
    return `${difference > 0 ? '+' : ''}${formatCount(difference)} ${callsNoun(difference)}`;
  }

  if (typeCalls === 0 || previousTypeCalls === 0) {
    return '—';
  }

  const points = (calls / typeCalls - previousCalls / previousTypeCalls) * 100;
  return `${points > 0 ? '+' : ''}${points.toFixed(1).replace('.', ',')} п.п.`;
};

/**
 * Динамика выбранного итога внутри типа с наложением прошлого периода той же длины.
 * Родитель рисует карточку только при выбранном периоде, поэтому `previous` определён всегда.
 */
const PairTrendChart = ({ dateRange, callType, typeChart, outcomeChart, pair }) => {
  const panels = usePanelSettings();
  const panelId = PANEL_IDS['ui:call-paths.pair-trend'];
  const [granularity, setGranularity] = useState(() => defaultGranularity(dateRange));
  const [metric, setMetric] = useState('calls');
  const previous = useMemo(() => previousPeriod(dateRange), [dateRange]);

  const params = useMemo(() => {
    const slice = (from, to) => ({
      date_from: format(from, DATE_FORMAT),
      date_to: format(to, DATE_FORMAT),
      call_type: callType === 'all' ? undefined : callType
    });

    return {
      typeChart,
      outcomeChart,
      typeFilter: categoryFilter(typeChart, pair.type.code),
      current: slice(dateRange.from, dateRange.to),
      previous: slice(previous.from, previous.to)
    };
  }, [typeChart, outcomeChart, pair.type.code, dateRange, previous, callType]);

  const { data, isLoading, error } = useNodeRequest({
    fetcher: fetchPairSeries,
    params,
    errorText: 'Не удалось загрузить динамику.'
  });

  const isPending = isLoading || (!data && !error);

  const { rows, totals } = useMemo(
    () => buildPairTrend({ series: data, dateRange, granularity, typeCode: pair.type.code, outcomeCode: pair.outcome.code }),
    [data, dateRange, granularity, pair.type.code, pair.outcome.code]
  );

  const comparison = metric === 'calls'
    ? {
        current: `${formatCount(totals.calls)} ${callsNoun(totals.calls)}`,
        previous: `${formatCount(totals.previousCalls)} ${callsNoun(totals.previousCalls)}`
      }
    : {
        current: formatPercent(totals.calls, totals.typeCalls),
        previous: formatPercent(totals.previousCalls, totals.previousTypeCalls)
      };

  return (
    <Card data-panel-id={PANEL_IDS['ui:call-paths.pair-trend']} className="min-w-0">
      <CardHeader className="flex flex-col gap-3 space-y-0 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <CardTitle className="text-base">{panels.title(panelId)}</CardTitle>
            <InfoHint text={panels.description(panelId)} label="Как считается динамика" trackId="call_paths.pair_trend.hint" />
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{pair.type.label} → {pair.outcome.label}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <GranularitySelect trackId="call_paths.pair_trend.granularity" label="Шаг динамики" value={granularity} onChange={setGranularity} />
          <MetricToggle trackId="call_paths.pair_trend.metric" options={METRICS} value={metric} onChange={setMetric} />
        </div>
      </CardHeader>
      <CardContent>
        {error && <ErrorText>{error}</ErrorText>}
        {!error && isPending && <MutedText>Загружаем динамику…</MutedText>}
        {!error && !isPending && (
          <>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">
              <span>
                <span className="mr-1.5 inline-block h-0.5 w-4 align-middle" style={{ backgroundColor: PRIMARY_COLOR }} />
                Текущий период: <strong>{comparison.current}</strong>
              </span>
              <span>
                <span className="mr-1.5 inline-block w-4 border-t-2 border-dashed align-middle" style={{ borderColor: SEMANTIC_COLORS.neutral }} />
                Предыдущий период: <strong>{comparison.previous}</strong>
              </span>
              <span>Изменение: <strong>{describeDifference(metric, totals)}</strong></span>
              <InfoHint
                text={`Предыдущий период той же длины: ${format(previous.from, 'dd.MM.yyyy')}–${format(previous.to, 'dd.MM.yyyy')}. На графике периоды сопоставлены по порядку дней.`}
                label="О сравнении периодов"
                trackId="call_paths.pair_trend.comparison_hint"
              />
            </div>
            <div className="mt-5 h-60 min-w-0">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={160}>
                <LineChart data={rows} margin={{ top: 8, right: 20, bottom: 8, left: -12 }}>
                  <CartesianGrid {...AXIS.grid} vertical={false} />
                  <XAxis dataKey="label" tick={AXIS.tick} minTickGap={18} />
                  <YAxis
                    allowDecimals={false}
                    domain={metric === 'share' ? [0, 100] : [0, 'auto']}
                    tick={AXIS.tick}
                    tickFormatter={metric === 'share' ? (value) => `${value}%` : undefined}
                  />
                  <Tooltip
                    wrapperStyle={AXIS.tooltipWrapperStyle}
                    formatter={(value, name, item) => [
                      metric === 'share' ? `${value}%` : formatCount(value),
                      name === 'previous' ? `Предыдущий период · ${item.payload.previousLabel}` : 'Текущий период'
                    ]}
                  />
                  <Line type="monotone" dataKey={metric} name="current" stroke={PRIMARY_COLOR} strokeWidth={2.5} dot={{ r: 2 }} activeDot={{ r: 4 }} isAnimationActive={false} />
                  <Line
                    type="monotone"
                    dataKey={metric === 'calls' ? 'previousCalls' : 'previousShare'}
                    name="previous"
                    stroke={SEMANTIC_COLORS.neutral}
                    strokeWidth={2}
                    strokeDasharray="5 4"
                    dot={false}
                    activeDot={{ r: 4 }}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PairTrendChart;
